import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  RefreshCw,
  Send,
  ExternalLink,
  FileText,
  Search,
  Sparkles,
  CheckCircle,
  Shield,
  AlertCircle,
} from "lucide-react";
import api from "../api/client";
import ScoreRing from "./ScoreRing";

export default function ContentDetail({ selectedSite }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("content");
  const [publishing, setPublishing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [updatingReview, setUpdatingReview] = useState(false);

  const fetchItem = () => {
    api
      .get(`/content/${id}/`)
      .then((res) => setItem(res.data))
      .catch(() => navigate("/content"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchItem();
  }, [id]);

  // Navigate back to list when site changes
  useEffect(() => {
    if (item && selectedSite && item.website !== selectedSite.id) {
      navigate("/content");
    }
  }, [selectedSite?.id]);

  // Auto-refresh while processing
  useEffect(() => {
    if (!item) return;
    const active = ["queued", "analyzing", "generating", "publishing"].includes(item.status);
    if (!active) return;
    const interval = setInterval(fetchItem, 5000);
    return () => clearInterval(interval);
  }, [item?.status]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      await api.post(`/content/${id}/publish/`);
      fetchItem();
    } catch {}
    setPublishing(false);
  };

  const handleRegenerate = async (skipSerp = false) => {
    setRegenerating(true);
    try {
      await api.post(`/content/${id}/regenerate/`, { skip_serp: skipSerp });
      // Immediately update local state so processing banner shows
      setItem((prev) => prev ? { ...prev, status: "queued" } : prev);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to regenerate");
    }
    setRegenerating(false);
  };

  const updateReviewStatus = async (newStatus) => {
    setUpdatingReview(true);
    try {
      await api.patch(`/content/${id}/review-status/`, { review_status: newStatus });
      fetchItem();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update review status");
    }
    setUpdatingReview(false);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-10 bg-dark-300 rounded-lg w-64" />
        <div className="h-96 bg-dark-300 rounded-xl" />
      </div>
    );
  }

  if (!item) return null;

  const tabs = [
    { key: "content", label: "Content", icon: FileText },
    { key: "serp", label: "SERP Data", icon: Search },
    { key: "blueprint", label: "Blueprint", icon: Sparkles },
  ];

  const isProcessing = ["queued", "analyzing", "generating", "publishing"].includes(item.status);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/content")}
            className="p-2 hover:bg-dark-50/50 rounded-lg transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-400" />
          </button>
          <div>
            <h2 className="text-lg font-semibold font-mono">{item.target_keyword}</h2>
            <div className="text-xs text-gray-500 flex items-center gap-2">
              {item.target_location && <><span>{item.target_location}</span><span>·</span></>}
              <span>{item.website_domain}</span>
              {item.review_status && (
                <>
                  <span>·</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                    item.review_status === "reviewed" ? "bg-green-900/30 text-green-400" :
                    item.review_status === "needs_edit" ? "bg-red-900/30 text-red-400" :
                    "bg-amber-900/30 text-amber-400"
                  }`}>
                    {item.review_status === "ai_draft" ? "AI Draft" :
                     item.review_status === "reviewed" ? "Reviewed" : "Needs Edit"}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {item.content_score != null && (
            <ScoreRing score={item.content_score} size={48} strokeWidth={4} label="Score" />
          )}
          {item.status === "reviewing" && (
            <>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="flex items-center gap-2 px-3 py-2 bg-dark-300 border border-dark-50 text-sm text-gray-300 hover:text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw size={14} className={regenerating ? "animate-spin" : ""} />
                Regenerate
              </button>
              {item.review_status !== "reviewed" && (
                <button
                  onClick={() => updateReviewStatus("reviewed")}
                  disabled={updatingReview}
                  className="flex items-center gap-2 px-3 py-2 bg-green-900/30 border border-green-800/30 text-sm text-green-400 hover:bg-green-900/50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <Shield size={14} />
                  Mark Reviewed
                </button>
              )}
              {item.review_status === "reviewed" && (
                <button
                  onClick={() => updateReviewStatus("needs_edit")}
                  disabled={updatingReview}
                  className="flex items-center gap-2 px-3 py-2 bg-red-900/30 border border-red-800/30 text-sm text-red-400 hover:bg-red-900/50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <AlertCircle size={14} />
                  Needs Edit
                </button>
              )}
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                <Send size={14} />
                {publishing ? "Publishing..." : "Publish to WP"}
              </button>
            </>
          )}
          {item.published_url && (
            <a
              href={item.published_url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-green-900/30 text-green-400 text-sm rounded-lg"
            >
              <ExternalLink size={14} />
              View Live
            </a>
          )}
        </div>
      </div>

      {/* Processing indicator */}
      {isProcessing && (
        <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg px-4 py-3 flex items-center gap-3">
          <RefreshCw size={16} className="text-blue-400 animate-spin" />
          <span className="text-sm text-blue-300">
            {item.status === "queued" && "Waiting in queue..."}
            {item.status === "analyzing" && "Analyzing top Google results for this keyword..."}
            {item.status === "generating" && "AI is generating optimized content..."}
            {item.status === "publishing" && "Publishing to WordPress..."}
          </span>
        </div>
      )}

      {/* SERP skipped warning (shown on successful generation without SERP) */}
      {item.status === "reviewing" && item.serp_data?.serp_skipped && (
        <div className="bg-amber-900/20 border border-amber-800/30 rounded-lg px-4 py-3 flex items-start gap-3">
          <span className="text-amber-400 mt-0.5">&#9888;</span>
          <div className="flex-1">
            <div className="text-sm text-amber-400 font-medium">SERP analysis was skipped</div>
            <div className="text-xs text-amber-300/70 mt-1">
              {item.error_message || "Content was generated based on keyword and business context only — no competitor data was used."}
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {item.status === "failed" && item.error_message && (
        <div className="bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-3">
          <div className="text-sm text-red-400 font-medium">Pipeline Failed</div>
          <div className="text-xs text-red-300 mt-1">{item.error_message}</div>
          <div className="flex items-center gap-3 mt-2">
            <button
              onClick={() => handleRegenerate(false)}
              disabled={regenerating}
              className="text-xs text-copper hover:text-copper-300 disabled:opacity-50"
            >
              {regenerating ? "Retrying..." : "Try Again"}
            </button>
            <span className="text-gray-600">|</span>
            <button
              onClick={() => handleRegenerate(true)}
              disabled={regenerating}
              className="text-xs text-amber-400 hover:text-amber-300 disabled:opacity-50"
            >
              Skip SERP &amp; Generate
            </button>
          </div>
        </div>
      )}

      {/* Meta info */}
      {item.generated_meta_title && (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-2">
          <div>
            <span className="text-xs text-gray-500">Meta Title</span>
            <div className="text-sm text-blue-400 font-medium">{item.generated_meta_title}</div>
          </div>
          {item.generated_meta_description && (
            <div>
              <span className="text-xs text-gray-500">Meta Description</span>
              <div className="text-sm text-gray-300">{item.generated_meta_description}</div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-dark-300 border border-dark-50 rounded-lg p-1">
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
              activeTab === key
                ? "bg-dark-50 text-copper"
                : "text-gray-400 hover:text-white"
            }`}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
        {activeTab === "content" && (
          item.generated_content ? (
            <div
              className="prose prose-invert prose-sm max-w-none
                         prose-headings:text-gray-200 prose-headings:font-semibold
                         prose-p:text-gray-300 prose-p:leading-relaxed
                         prose-a:text-copper prose-strong:text-gray-200
                         prose-li:text-gray-300 prose-ul:text-gray-300"
              dangerouslySetInnerHTML={{ __html: item.generated_content }}
            />
          ) : (
            <div className="text-gray-500 text-center py-12">
              {isProcessing ? "Content will appear here once generated..." : "No content generated yet"}
            </div>
          )
        )}

        {activeTab === "serp" && (
          item.serp_data ? (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-300">Top Ranking Pages</h3>
              <div className="space-y-2">
                {(item.serp_data.organic_results || []).slice(0, 10).map((r, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm">
                    <span className="text-xs text-gray-600 font-mono w-5 text-right mt-0.5">
                      {r.position}
                    </span>
                    <div className="flex-1 min-w-0">
                      <a href={r.link} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 truncate block">
                        {r.title}
                      </a>
                      <div className="text-xs text-gray-500 truncate">{r.snippet}</div>
                    </div>
                  </div>
                ))}
              </div>
              {item.serp_data.people_also_ask?.length > 0 && (
                <>
                  <h3 className="text-sm font-semibold text-gray-300 mt-6">People Also Ask</h3>
                  <ul className="space-y-1">
                    {item.serp_data.people_also_ask.map((q, i) => (
                      <li key={i} className="text-sm text-gray-400">• {q}</li>
                    ))}
                  </ul>
                </>
              )}
              {item.serp_data.recommendations && (
                <>
                  <h3 className="text-sm font-semibold text-gray-300 mt-6">Recommendations</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="bg-dark/50 rounded-lg p-3">
                      <div className="text-xs text-gray-500">Target Words</div>
                      <div className="font-mono text-lg">{item.serp_data.recommendations.target_word_count}</div>
                    </div>
                    <div className="bg-dark/50 rounded-lg p-3">
                      <div className="text-xs text-gray-500">Target Headings</div>
                      <div className="font-mono text-lg">{item.serp_data.recommendations.target_headings}</div>
                    </div>
                    <div className="bg-dark/50 rounded-lg p-3">
                      <div className="text-xs text-gray-500">Target Images</div>
                      <div className="font-mono text-lg">{item.serp_data.recommendations.target_images}</div>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="text-gray-500 text-center py-12">
              {isProcessing ? "SERP analysis in progress..." : "No SERP data available"}
            </div>
          )
        )}

        {activeTab === "blueprint" && (
          item.content_blueprint ? (
            <div className="space-y-4">
              {item.content_blueprint.title && (
                <div>
                  <span className="text-xs text-gray-500">Planned Title</span>
                  <div className="text-sm font-medium text-gray-200">{item.content_blueprint.title}</div>
                </div>
              )}
              {item.content_blueprint.unique_angle && (
                <div>
                  <span className="text-xs text-gray-500">Unique Angle</span>
                  <div className="text-sm text-gray-300">{item.content_blueprint.unique_angle}</div>
                </div>
              )}
              {item.content_blueprint.outline && (
                <div>
                  <span className="text-xs text-gray-500">Content Outline</span>
                  <div className="mt-2 space-y-1.5">
                    {item.content_blueprint.outline.map((section, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <span className="text-gray-200 font-medium">{section.heading}</span>
                        {section.notes && (
                          <span className="text-gray-500 text-xs mt-0.5">— {section.notes}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {item.content_blueprint.keywords_to_include && (
                <div>
                  <span className="text-xs text-gray-500">Keywords to Include</span>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {item.content_blueprint.keywords_to_include.map((kw, i) => (
                      <span key={i} className="px-2 py-0.5 bg-dark-50 rounded text-xs text-gray-300">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-gray-500 text-center py-12">
              {isProcessing ? "Blueprint being generated..." : "No blueprint available"}
            </div>
          )
        )}
      </div>
    </div>
  );
}
